import { Link, useParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { useListQuery } from "@shared-core/domain/useListQuery";
import type { CustomerSession } from "@shared-core/data/customer-api-types";
import { usePortalViewer } from "./PortalViewerContext";
import { POLLARDS_PHONE } from "./portalContact";
import { OrderWithItemsBody } from "./order-detail/OrderWithItemsBody";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { ArrowLeft, AlertTriangle } from "lucide-react";

// The order page of a Tharstern-era order, one item or many (doc 48): one read for the whole order and all its
// items (`GET /views/customer/orders/{order_ref}/with-items`), laid out like the signed-in CIS order page by
// `OrderWithItemsBody`. The address is any one item's `order_ref`: a one-item order's own, a many-item order's
// first item's, or (an old link to one of its items) a later item's, which the body opens straight to.
// The page is read-only: a Tharstern job's proofs are not shown here (job-keyed proofs retired, order to invoice
// doc 85); a CIS order's are on its own order page. Nothing here says how we make the work: no supplier, route,
// cost or system name.

function OrderNotFound({
  session,
  backTo,
}: {
  session: CustomerSession | null;
  backTo: ReturnType<ReturnType<typeof usePortalViewer>["buildPortalUrl"]>;
}) {
  const customerName = session?.customer_name ?? "your company";
  return (
    <Card className="space-y-3 p-8 text-center">
      <p className="font-medium">We can&apos;t show you this order</p>
      {session?.visibility === "own_work" ? (
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          It either doesn&apos;t exist, or it wasn&apos;t placed by you. Orders
          placed by colleagues aren&apos;t included. To see all of{" "}
          {customerName}&apos;s work, ask your Pollards contact to switch it on
          for you, or call{" "}
          <span className="whitespace-nowrap">{POLLARDS_PHONE}</span>.
        </p>
      ) : (
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          It either doesn&apos;t exist, or it isn&apos;t one of {customerName}
          &apos;s orders. If you think it should be, call{" "}
          <span className="whitespace-nowrap">{POLLARDS_PHONE}</span>.
        </p>
      )}
      <div>
        <Button asChild variant="outline">
          <Link to={backTo}>Back to orders</Link>
        </Button>
      </div>
    </Card>
  );
}

export function PortalOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const adapter = useCustomerDataAdapter();
  const { viewKey, buildPortalUrl, sessionState } = usePortalViewer();

  const { state, retry } = useListQuery(
    () => adapter.getOrderWithItems(orderId ?? ""),
    [adapter, viewKey, orderId],
  );
  const session = sessionState.status === "success" ? sessionState.data : null;
  const ordersTo = buildPortalUrl(paths.portalOrders());

  if (state.status === "loading") {
    return (
      <Card
        aria-busy="true"
        className="p-8 text-center text-sm text-muted-foreground"
      >
        Loading order…
      </Card>
    );
  }

  if (state.status === "error") {
    return (
      <Card className="space-y-3 p-8 text-center">
        <AlertTriangle className="mx-auto size-6 text-muted-foreground" />
        <p className="font-medium">We couldn&apos;t load this order</p>
        <p className="text-sm text-muted-foreground">{state.message}</p>
        <Button variant="outline" onClick={retry}>
          Try again
        </Button>
      </Card>
    );
  }

  const order = state.data;
  if (!order) return <OrderNotFound session={session} backTo={ordersTo} />;

  return (
    <OrderWithItemsBody
      backLink={
        <Link
          to={ordersTo}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Orders
        </Link>
      }
      order={order}
      customerName={session?.customer_name ?? null}
    />
  );
}
